import { API_BASE, authHeaders } from './apiAuth'

/**
 * Connect Miro: letting the server place pictures on your own boards
 * -------------------------------------------------------------
 * Given once, on the website's Connect Miro page, and taken back there. The
 * token itself never comes to the browser; these calls only start the
 * connection, finish it with the code Miro hands back, and ask how it stands.
 * The Worker side, and why it is built this way: workers/api/src/miroTokens.ts.
 */

const ENDPOINT = `${API_BASE}/api/miro-connect`

export interface MiroConnection {
  connected: boolean
  /** Connected once, but Miro no longer accepts it. */
  needsReconnect: boolean
  miroUserId: string
  teamId: string
  teamName: string
  connectedAt: string
  /** The Miro account given is not the one linked to this person's panel. */
  otherAccount: boolean
  /** Only when asked to check with Miro. */
  check?: { ok: boolean; userName?: string; teamName?: string; scopes?: string[]; error?: string }
}

async function answer(response: Response, doing: string): Promise<unknown> {
  if (response.ok) return response.json()
  let reason = ''
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string') reason = body.error
  } catch {
    reason = ''
  }
  throw new Error(reason || `Could not ${doing} (HTTP ${response.status})`)
}

function asConnection(raw: unknown): MiroConnection {
  const value = (raw ?? {}) as Partial<MiroConnection>
  return {
    connected: value.connected === true,
    needsReconnect: value.needsReconnect === true,
    miroUserId: typeof value.miroUserId === 'string' ? value.miroUserId : '',
    teamId: typeof value.teamId === 'string' ? value.teamId : '',
    teamName: typeof value.teamName === 'string' ? value.teamName : '',
    connectedAt: typeof value.connectedAt === 'string' ? value.connectedAt : '',
    otherAccount: value.otherAccount === true,
    check: value.check,
  }
}

/** How it stands. With `check`, Miro is asked whether the access still works. */
export async function getMiroConnection(check = false): Promise<MiroConnection> {
  const response = await fetch(`${ENDPOINT}${check ? '?check=1' : ''}`, {
    headers: await authHeaders('web'),
  })
  return asConnection(await answer(response, 'read the Miro connection'))
}

/** Where to send the person to allow it. The page then leaves for Miro. */
export async function startMiroConnection(): Promise<string> {
  const response = await fetch(`${ENDPOINT}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: '{}',
  })
  const body = (await answer(response, 'start connecting Miro')) as { url?: unknown }
  if (typeof body.url !== 'string' || !body.url.startsWith('https://miro.com/')) {
    throw new Error('The server did not say where to go')
  }
  return body.url
}

/** Back from Miro's page with a code: the server exchanges and keeps it. */
export async function finishMiroConnection(code: string, state: string): Promise<MiroConnection> {
  const response = await fetch(`${ENDPOINT}/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({ code, state }),
  })
  return asConnection(await answer(response, 'finish connecting Miro'))
}

export async function disconnectMiro(): Promise<MiroConnection> {
  const response = await fetch(ENDPOINT, { method: 'DELETE', headers: await authHeaders('web') })
  return asConnection(await answer(response, 'disconnect Miro'))
}
